import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { theme } from '../theme';
import { routes } from '../config/environment';

// Importar componentes
import LoginPage from '../pages/LoginPage';
import OTPVerificationPage from '../pages/OTPVerificationPage';
import ContestPage from '../pages/ContestPage';
import ContestVotePage from '../pages/ContestVotePage';
import ContestParticipantPage from '../pages/ContestParticipantPage';
import ContestCouponPassesPage from '../pages/ContestCouponPassesPage';
import ContestPassCodesPage from '../pages/ContestPassCodesPage';
import SeoConfigPage from '../pages/SeoConfigPage';
import PetWinnersAndCouponsPage from '../pages/PetWinnersAndCouponsPage';
import PlatformSettingsPage from '../pages/PlatformSettingsPage';
import PayoutsCenterPage from '../pages/PayoutsCenterPage';
import { I18nProvider } from '../i18n';
import AppDialogProvider from '../components/AppDialogProvider';
import { Analytics } from '../Analytics';
import { usePageView } from '../usePageView';
import { useSeoMetadata } from '../hooks/useSeoMetadata';
import '../utils/verifyGA4'; // Carga la función de verificación

// Componente interno que usa el hook usePageView
const RouterWithAnalytics: React.FC = () => {
  // Hook que envía page_view cada vez que cambia la ruta
  usePageView();
  
  // Aplicar SEO metadata global (se aplicará a todas las páginas por defecto)
  useSeoMetadata({ useGlobalSeo: true });

  return (
    <Routes>
            {/* Ruta por defecto - redirige a contest_test */}
            <Route path="/" element={<Navigate to={routes.contest} replace />} />
            
            {/* Ruta de login */}
            <Route path={routes.login} element={<LoginPage />} />
            
            {/* Ruta de verificación OTP */}
            <Route path={routes.otp} element={<OTPVerificationPage />} />
            
            {/* Ruta principal del concurso */}
            <Route path={routes.contest} element={<ContestPage />} />
            
            {/* Ruta de votación */}
            <Route path={routes.contestVote} element={<ContestVotePage />} />
            
            {/* Ruta de ganadores — redirige al centro de pagos unificado */}
            <Route path={routes.contestWinners} element={<Navigate to={routes.payoutsCenter} replace />} />
            
            {/* Centro de pagos: premios de concurso + retiros de wallet */}
            <Route path={routes.payoutsCenter} element={<PayoutsCenterPage />} />
            
            {/* Ruta para participantes - unirse a concursos */}
            <Route path={routes.contestParticipant} element={<ContestParticipantPage />} />
            
            {/* Ruta para listar CouponPasses */}
            <Route path={routes.contestCouponPasses} element={<ContestCouponPassesPage />} />
            
            {/* Ruta para listar Pass Codes */}
            <Route path={routes.contestPassCodes} element={<ContestPassCodesPage />} />
            
            {/* Ruta de configuración SEO */}
            <Route path={routes.seoConfig} element={<SeoConfigPage />} />
            
            {/* Ruta de ganadores y cupones */}
            <Route path={routes.petWinnersAndCoupons} element={<PetWinnersAndCouponsPage />} />
            
            {/* Platform economics & withdrawal settings */}
            <Route path="/platform_settings" element={<PlatformSettingsPage />} />

            {/* Ruta de fallback - redirige a contest_test */}
            <Route path="*" element={<Navigate to={routes.contest} replace />} />
    </Routes>
  );
};

// Componente principal del router
const AppRouter: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <I18nProvider>
        <AppDialogProvider>
          <Router>
            {/* Carga y configura GA una sola vez */}
            <Analytics />

            {/* Maneja todas las rutas y envía pageviews */}
            <RouterWithAnalytics />
          </Router>
        </AppDialogProvider>
      </I18nProvider>
    </ThemeProvider>
  );
};

export default AppRouter;
